import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StatusPill } from "@/components/StatusPill";
import { SelectField } from "@/pages/FacilitiesPage";
import { toast } from "sonner";
import { Plus, Trash2, DollarSign } from "lucide-react";

export default function ClaimsPage() {
  const [claims, setClaims] = useState([]);
  const [facs, setFacs] = useState([]);
  const [nps, setNps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_name: "", amount: 0, submitted_date: "", status: "submitted", facility_id: "", np_id: "", notes: "" });

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: f }, { data: n }] = await Promise.all([
        api.get("/claims"), api.get("/facilities"), api.get("/nps"),
      ]);
      setClaims(c); setFacs(f); setNps(n);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[claims] load failed:", e?.message || e);
      toast.error("Could not load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const facMap = Object.fromEntries(facs.map((f) => [f.id, f.name]));
  const npMap = Object.fromEntries(nps.map((n) => [n.id, n.name]));
  const filtered = claims.filter((c) => filter === "all" ? true : c.status_light === filter);

  const total = filtered.reduce((s, c) => s + (Number(c.amount) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/claims", { ...form, amount: Number(form.amount) || 0, facility_id: form.facility_id || null, np_id: form.np_id || null });
      toast.success("Claim added"); setOpen(false);
      setForm({ patient_name: "", amount: 0, submitted_date: "", status: "submitted", facility_id: "", np_id: "", notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (c) => { if (!window.confirm("Remove?")) return; await api.delete(`/claims/${c.id}`); load(); };
  const setStatus = async (c, status) => { await api.put(`/claims/${c.id}`, { status }); load(); };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Workflow</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Claims / Billing</h1>
          <p className="text-sm text-[#5C5850] mt-1.5">{claims.length} claims tracked · showing ${total.toFixed(2)}</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="add-claim-btn">
          <Plus className="h-4 w-4 mr-2"/> New claim
        </Button>
      </div>

      <div className="mt-6 flex gap-2 text-xs">
        {[["all","All","gray"],["red","Attention","red"],["orange","7–30 days","orange"],["green","On track","green"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-3 py-2 rounded-md border transition-colors ${filter === v ? "bg-[#4A6B5D] text-white border-[#4A6B5D]" : "bg-white border-[#E5E0D8] text-[#5C5850] hover:bg-[#F0EBE1]"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-[#F7F5F0]">
            <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Status</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Patient</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Facility</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] text-[#5C5850]">NP</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Submitted</th>
            <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Amount</th>
            <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-[#8A847A] text-sm"><DollarSign className="h-6 w-6 mx-auto opacity-60 mb-2"/>No claims.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-[#E5E0D8] table-row-hover">
                <td className="px-4 py-3"><StatusPill status={c.status_light} label={c.status} /></td>
                <td className="px-4 py-3 text-sm">{c.patient_name || "—"}</td>
                <td className="px-4 py-3 text-sm">{c.facility_id ? <Link to={`/facilities/${c.facility_id}`} className="text-[#4A6B5D]">{facMap[c.facility_id]}</Link> : "—"}</td>
                <td className="px-4 py-3 text-sm">{c.np_id ? npMap[c.np_id] : "—"}</td>
                <td className="px-4 py-3 text-sm">{c.submitted_date || "—"}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">${(c.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {c.status !== "paid" && <Button variant="ghost" size="sm" onClick={() => setStatus(c, "paid")} className="text-[#507A5E] text-xs">Paid</Button>}
                    {c.status !== "needs_attention" && <Button variant="ghost" size="sm" onClick={() => setStatus(c, "needs_attention")} className="text-[#8a5f1e] text-xs">Flag</Button>}
                    <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-[#C17D6E]"><Trash2 className="h-4 w-4"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle className="heading text-2xl">New claim</SheetTitle></SheetHeader>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Patient</Label>
              <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]"/>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Amount</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]"/>
              </div>
              <div><Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Submitted date</Label>
                <Input type="date" value={form.submitted_date} onChange={(e) => setForm({ ...form, submitted_date: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]"/>
              </div>
            </div>
            <SelectField label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={[{value:"submitted",label:"Submitted"},{value:"paid",label:"Paid"},{value:"denied",label:"Denied"},{value:"needs_attention",label:"Needs attention"}]} />
            <SelectField label="Facility" value={form.facility_id} onChange={(v) => setForm({ ...form, facility_id: v })} options={[{value:"",label:"— none —"}, ...facs.map((f) => ({value: f.id, label: f.name}))]} />
            <SelectField label="NP" value={form.np_id} onChange={(v) => setForm({ ...form, np_id: v })} options={[{value:"",label:"— none —"}, ...nps.map((n) => ({value: n.id, label: n.name}))]} />
            <div><Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 bg-white border-[#E5E0D8]"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#4A6B5D] hover:bg-[#3E594D]">Create</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
